<template>
  <div class="app-shell" :class="{ 'is-home-layout': isHome, 'is-auth-layout': isAuthPage }">
    <el-header class="layout-header-wrap" v-if="!isAuthPage">
      <div class="layout-header">
        <!-- Logo -->
        <div class="logo" @click="go('/')">{{ siteName }}</div>

        <!-- Desktop Navigation -->
        <div class="desktop-nav">
          <el-menu
            mode="horizontal"
            :default-active="activeRoute"
            router
            :ellipsis="false"
            :default-openeds="defaultOpeneds"
          >
            <template v-for="(item, index) in navLinks" :key="item.path || item.index">
              <el-sub-menu
                v-if="item.type === 'group'"
                :index="item.index"
                :trigger="item.trigger"
                :class="['nav-menu-entry', 'nav-priority-' + (index + 1)]"
              >
                <template #title>
                  <span>{{ item.title }}</span>
                </template>
                <el-menu-item v-for="child in item.children" :key="child.path" :index="child.path">
                  <el-icon v-if="child.icon"><component :is="child.icon" /></el-icon>
                  <span>{{ child.title }}</span>
                  <span v-if="shouldShowNotificationBadge(child)" class="notification-nav-dot" />
                </el-menu-item>
              </el-sub-menu>
              <el-menu-item
                v-else
                :index="item.path"
                :class="['nav-menu-entry', 'nav-priority-' + (index + 1)]"
              >
                <el-icon v-if="item.icon"><component :is="item.icon" /></el-icon>
                <span>{{ item.title }}</span>
                <span v-if="shouldShowNotificationBadge(item)" class="notification-nav-dot" />
              </el-menu-item>
            </template>
          </el-menu>
        </div>

        <div class="header-actions">
          <!-- Theme Toggle -->
          <el-button
            class="theme-toggle"
            :icon="isDark ? Sunny : Moon"
            circle
            text
            @click="toggleTheme"
          />

          <!-- Mobile Nav Trigger -->
          <div class="mobile-nav" v-if="authReady && isLogged">
            <el-button
              @click="drawer = true"
              :icon="MenuIcon"
              text
              circle
              class="mobile-menu-btn"
            />
          </div>

          <AccountMenu
            v-if="authReady && isLogged"
            :avatar-src="customAvatar || ''"
            :account-name="accountName"
            :role-label="accountRoleLabel"
            :can-access-admin="canAccessAdmin"
            @navigate="go"
            @logout="logout"
          />

          <!-- Auth Buttons -->
          <template v-if="authReady && !isLogged">
            <el-button type="primary" @click="go('/login')">登录</el-button>
            <el-button @click="go('/register')" class="hero-register-btn ml-2"> 注册 </el-button>
          </template>
        </div>
      </div>
    </el-header>

    <!-- Mobile Drawer -->
    <el-drawer v-model="drawer" title="导航菜单" direction="ltr" size="280px" class="mobile-drawer">
      <el-menu :default-active="activeRoute" router @select="drawer = false" class="drawer-menu">
        <template v-for="(item, index) in drawerLinks" :key="index">
          <el-divider v-if="item.isDivider" class="nav-divider" />
          <el-menu-item v-else :index="item.path">
            <el-icon v-if="item.icon"><component :is="item.icon" /></el-icon>
            <span>{{ item.title }}</span>
            <span v-if="shouldShowNotificationBadge(item)" class="notification-nav-dot" />
          </el-menu-item>
        </template>
      </el-menu>
    </el-drawer>

    <main
      class="app-main"
      :style="{
        '--footer-height': footerHeight + 'px',
        '--home-center-offset': homeCenterOffset,
        '--home-content-center-y': homeContentCenterY,
      }"
    >
      <slot />
    </main>

    <AppFooter
      v-if="showFooter"
      ref="footerRef"
      :variant="isHome ? 'home' : 'standard'"
      :footer-text="footerText"
      :filing-icp="filingIcp"
      :filing-icp-link="filingIcpLink"
      :filing-mps="filingMps"
      :filing-mps-link="filingMpsLink"
      :repo-url="repoUrl"
      :repo-label="repoLabel"
    />
  </div>
</template>

<script setup lang="ts">
import { Menu as MenuIcon, Moon, Sunny } from '@element-plus/icons-vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import AccountMenu from '@/components/layout/AccountMenu.vue'
import { useAppLayoutState } from '@/components/layout/useAppLayoutState'

const {
  customAvatar,
  isDark,
  toggleTheme,
  isHome,
  isAuthPage,
  siteName,
  authReady,
  drawer,
  footerText,
  filingIcp,
  filingIcpLink,
  filingMps,
  filingMpsLink,
  footerHeight,
  footerRef,
  homeCenterOffset,
  homeContentCenterY,
  isLogged,
  canAccessAdmin,
  defaultOpeneds,
  navLinks,
  drawerLinks,
  activeRoute,
  showFooter,
  repoUrl,
  repoLabel,
  accountRoleLabel,
  accountName,
  go,
  logout,
  shouldShowNotificationBadge,
} = useAppLayoutState()
</script>

<style>
.app-shell :where(.page-header) {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 20px;
}

.app-shell :where(.page-header-content h1) {
  font-size: 32px;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, var(--color-heading) 0%, #409eff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.app-shell :where(.page-header-content p) {
  margin: 0;
  color: var(--color-text-light);
  font-size: 16px;
  transition: color 0.3s ease;
}

.app-shell :where(.page-header-actions) {
  display: flex;
  gap: 12px;
}

.app-shell :where(.form-tip) {
  font-size: 12px;
  color: var(--color-text-light);
  margin-top: 6px;
  line-height: 1.4;
}

.app-shell :where(.pagination-container) {
  margin-top: 32px;
  padding-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  animation: fadeIn 0.6s ease;
}
</style>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Home Mode Shell */
.is-home-layout {
  min-height: 100vh;
  position: fixed;
  inset: 0;
  overflow: hidden;
}

.layout-header-wrap {
  padding: 0 20px;
  background: var(--color-header-background);
  backdrop-filter: blur(8px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid var(--color-border);
  height: 64px;
  z-index: 100;
  flex-shrink: 0;
}

.is-home-layout .layout-header-wrap {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: transparent;
  border-bottom: none;
  box-shadow: none;
  backdrop-filter: none;
  z-index: 20;
}

/* Home Layout UI Enforcement - Scoped to .layout-header */
.is-home-layout .layout-header .logo,
.is-home-layout .layout-header :deep(.account-name),
.is-home-layout .layout-header .theme-toggle,
.is-home-layout .layout-header .mobile-menu-btn,
.is-home-layout .layout-header :deep(.el-menu-item),
.is-home-layout .layout-header :deep(.el-sub-menu__title) {
  color: #fff !important;
}

.is-home-layout .layout-header :deep(.account-trigger:hover),
.is-home-layout .layout-header .logo:hover,
.is-home-layout .layout-header .theme-toggle:hover,
.is-home-layout .layout-header .mobile-menu-btn:hover,
.is-home-layout .layout-header :deep(.el-menu-item:hover),
.is-home-layout .layout-header :deep(.el-menu-item.is-active),
.is-home-layout .layout-header :deep(.el-sub-menu__title:hover),
.is-home-layout .layout-header :deep(.el-sub-menu__title.is-active) {
  background-color: rgba(255, 255, 255, 0.15) !important;
  color: #fff !important;
}

.is-home-layout .header-actions :deep(.el-button--primary) {
  background: rgba(64, 158, 255, 0.3) !important;
  border: 1px solid rgba(64, 158, 255, 0.4) !important;
  color: #fff !important;
  border-radius: 8px;
}
.is-home-layout .hero-register-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  color: #fff !important;
  border-radius: 8px;
  height: 32px;
  padding: 0 15px;
  font-size: 14px;
}

/* Mobile Drawer reset - Respect Global Theme */
.mobile-drawer :deep(.el-menu) {
  border-right: none;
  background: transparent;
}
.mobile-drawer :deep(.el-menu-item) {
  color: var(--color-text);
  border-radius: 8px;
  margin: 4px 8px;
  height: 44px;
  line-height: 44px;
}
.mobile-drawer :deep(.el-menu-item.is-active) {
  background-color: rgba(64, 158, 255, 0.1);
  color: var(--el-color-primary);
  font-weight: 600;
}

.layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  gap: 12px;
}
.logo {
  font-weight: 700;
  font-size: 20px;
  color: var(--color-heading);
  cursor: pointer;
  border-radius: 8px;
  padding: 4px 8px;
  transition: background-color 0.2s;
  flex-shrink: 0;
}
.logo:hover {
  color: var(--el-color-primary);
}

.desktop-nav {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
  height: 100%;
}
.desktop-nav .el-menu {
  border-bottom: none;
  height: 100%;
  background: transparent;
  max-width: 100%;
  overflow: hidden;
}

.desktop-nav :deep(.nav-menu-entry) {
  flex-shrink: 0;
}

.desktop-nav :deep(.el-sub-menu__title) {
  border-bottom: 2px solid transparent;
  transition:
    color 0.2s,
    border-color 0.2s;
}
.desktop-nav :deep(.el-sub-menu__title:hover) {
  color: var(--el-color-primary);
}
.notification-nav-dot {
  width: 8px;
  height: 8px;
  margin-left: 2px;
  border-radius: 999px;
  background: var(--el-color-danger);
  box-shadow: 0 0 0 2px var(--color-header-background);
  flex-shrink: 0;
}
.is-home-layout .notification-nav-dot {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.28);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.theme-toggle {
  font-size: 20px;
  border-radius: 8px;
}

.app-main {
  --header-height: 64px;
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
  transition: padding 0.3s ease;
}
.is-home-layout .app-main {
  position: fixed;
  inset: 0;
  z-index: 0;
  padding: 0;
  flex: none;
  height: 100vh;
  min-height: 100vh;
  background: transparent;
}
.is-auth-layout .app-main {
  padding: 0 !important;
}

.filing-icon {
  width: 13px;
}

@media (max-width: 1440px) {
  .desktop-nav :deep(.nav-priority-8) {
    display: none;
  }
}

@media (max-width: 1360px) {
  .desktop-nav :deep(.nav-priority-7) {
    display: none;
  }
}

@media (max-width: 1280px) {
  .desktop-nav :deep(.nav-priority-6) {
    display: none;
  }
}

@media (max-width: 1180px) {
  .desktop-nav :deep(.nav-priority-5) {
    display: none;
  }
}

@media (max-width: 1060px) {
  .desktop-nav :deep(.nav-priority-4) {
    display: none;
  }
}

@media (max-width: 940px) {
  .desktop-nav :deep(.nav-priority-3) {
    display: none;
  }
}

@media (max-width: 840px) {
  .desktop-nav :deep(.nav-priority-2) {
    display: none;
  }

  :deep(.account-name) {
    display: none;
  }
}

@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }
}
</style>
